<template>
  <div>
    <template slot="header">
      <div class="row">
        <div class="col-6">
          <h3 class="mb-0">{{ $t("EQUIPMENTS.EQUIPMENTS_LIST") }}</h3>
        </div>
        <div class="col-6 text-right">
          <base-button
            type="primary"
            icon
            size="sm"
            @click="addEquipment()"
            v-if="$currentUserCan($permissions.PERM_CREATE_EQUIPMENTS)"
          >
            <span class="btn-inner--icon">
              <i class="fas fa-plus"></i>
            </span>
            <span class="btn-inner--text">
              {{ $t("EQUIPMENTS.ADD_EQUIPMENT") }}
            </span>
          </base-button>
        </div>
      </div>
    </template>
    <div>
      <div
        class="col-12 d-flex flex-wrap filter-row"
      >
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <establishment-selector
          v-if="
            !filterEstablishment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)
          "
          @establishmentChanged="
            (establishmentId) => (selectedEstablishment = establishmentId)
          "
        />

        <category-equipment-selector
          v-if="
            !filterCategoryEquipment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_CATEGORY_EQUIPMENTS)
          "
          @categoryEquipmentChanged="
            (categoryEquipmentId) =>
              (selectedCategoryEquipment = categoryEquipmentId)
          "
        />

        <el-select
          class="select-primary"
          v-model="selectedIsInternal"
          :placeholder="$t('COMMON.BELONG_TO_LANIEL')"
        >
          <el-option
            class="select-primary"
            :key="0"
            :label="$t('COMMON.BELONG_TO_LANIEL_OR_NOT')"
            :value="null"
          >
          </el-option>
          <el-option
            class="select-primary"
            :key="1"
            :label="$t('COMMON.BELONG_TO_LANIEL')"
            :value="true"
          >
          </el-option>
          <el-option
            class="select-primary"
            :key="2"
            :label="$t('COMMON.DO_NOT_BELONG_TO_LANIEL')"
            :value="false"
          >
          </el-option>
        </el-select>

        <el-select
          class="select-primary"
          v-model="selectedLoanStatus"
          :placeholder="$t('EQUIPMENT_TRANSFERS.LOAN_STATUS')"
        >
          <el-option
            class="select-primary"
            :key="0"
            :label="$t('EQUIPMENT_TRANSFERS.LOAN_STATUS')"
            :value="null"
          >
          </el-option>
          <el-option
            class="select-primary"
            :key="1"
            :label="$t('EQUIPMENT_TRANSFERS.AVAILABLE')"
            :value="'AVAILABLE'"
          >
          </el-option>
          <el-option
            class="select-primary"
            :key="2"
            :label="$t('EQUIPMENT_TRANSFERS.ON_LOAN')"
            :value="'ON_LOAN'"
          >
          </el-option>
        </el-select>

        <organization-selector
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          @organizationChanged="
            (organizationId) => (selectedOrganization = organizationId)
          "
        />

        <div>
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH_HINT.NAME_OR_CODE')"
            clearable
          />
        </div>
      </div>

      <el-table
        class="align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="equipments"
        @sort-change="sortChange"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>
        <el-table-column
          :label="$t('COMMON.ESTABLISHMENTS')"
          v-if="
            !filterEstablishment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)
          "
          min-width="300px"
        >
          <template v-slot="{ row }">
            <object-link :object="row.establishment" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.CATEGORY_EQUIPMENTS')"
          v-if="
            !filterEstablishment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_CATEGORY_EQUIPMENTS)
          "
          min-width="300px"
        >
          <template v-slot="{ row }">
            <object-link :object="row.categoryEquipment" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.NAME')"
          prop="name"
          sortable="custom"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <span>
              {{ `${row.name}` }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.BELONG_TO_LANIEL')"
          prop="name"
          sortable="custom"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <span>
              {{ row.is_internal ? $t("COMMON.YES") : $t("COMMON.NO") }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.STATUS')"
          prop="status"
          sortable="custom"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <span>
              {{
                row.status
                  ? $t("COMMON.EQUIPMENT_STATUS_" + row.status.toUpperCase())
                  : $t("COMMON.NO_STATUS")
              }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('EQUIPMENT_TRANSFERS.LOANED_TO')"
          min-width="250px"
        >
          <template v-slot="{ row }">
            <object-link
              v-if="row.loan_status === 'ON_LOAN' && row.loanedToEstablishment"
              :object="row.loanedToEstablishment"
            />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.ORGANIZATION')"
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          min-width="250px"
        >
          <template v-slot="{ row }">
            <organization :organization="row.organization" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.CREATED_AT')"
          prop="created_at"
          sortable="custom"
          min-width="200px"
        >
          <template v-slot="{ row }">
            {{ $timeZoneDateFormat(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column min-width="100px" align="center" fixed="right">
          <div slot-scope="{ row }" class="table-actions">
            <el-tooltip
              :content="$t('COMMON.VIEW')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_VIEW_EQUIPMENTS)"
            >
              <a
                type="text"
                @click="viewEquipment(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-eye"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.EDIT')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_EDIT_EQUIPMENTS)"
            >
              <a
                type="text"
                @click="editEquipment(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-edit"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.DELETE')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_DELETE_EQUIPMENTS)"
            >
              <a
                type="text"
                @click="deleteEquipment(row.id)"
                class="table-action table-action-delete"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-trash"></i>
              </a>
            </el-tooltip>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}

          <span v-if="selectedRows.length">
            &nbsp; &nbsp;
            {{ $t("COMMON.X_LINES_SELECTED", { count: selectedRows.length }) }}
          </span>
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { BasePagination } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import CategoryEquipmentSelector from "@/components/CategoryEquipmentSelector.vue";

export default {
  name: "equipment-list-table",

  components: {
    BasePagination,
    OrganizationSelector,
    EstablishmentSelector,
    CategoryEquipmentSelector,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  props: {
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
    filterServiceCenter: {
      type: String,
      default: null,
      description: "Service center id",
    },
    filterEstablishment: {
      type: String,
      default: null,
      description: "Establishment id",
    },
    filterCafeteria: {
      type: String,
      default: null,
      description: "Cafeteria id",
    },
    filterCategoryEquipment: {
      type: String,
      default: null,
      description: "Category equipment id",
    },
  },

  data() {
    return {
      query: null,
      selectedRows: [],
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      equipments: [],
      loading: true,
      selectedOrganization: null,
      selectedEstablishment: null,
      selectedCategoryEquipment: null,
      selectedIsInternal: null,
      selectedLoanStatus: null,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    filterOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterServiceCenter: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterEstablishment: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterCafeteria: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedTags: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedLocations: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedEstablishment: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterCategoryEquipment: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedCategoryEquipment: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedIsInternal: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedLoanStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList() {
      try {
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include:
            "organization,establishment,categoryEquipment,loanedToEstablishment",
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: { ...params.filter, organization: this.filterOrganization },
          };
        }
        if (this.selectedOrganization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.selectedOrganization,
            },
          };
        }
        if (this.filterServiceCenter) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              serviceCenter: this.filterServiceCenter,
            },
          };
        }
        if (this.filterEstablishment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              establishment: this.filterEstablishment,
            },
          };
        }
        if (this.selectedEstablishment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              establishment: this.selectedEstablishment,
            },
          };
        }
        if (this.filterCafeteria) {
          params = {
            ...params,
            filter: { ...params.filter, cafeteria: this.filterCafeteria },
          };
        }
        if (this.selectedTags) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              tags: this.selectedTags.map((item) => item.id),
            },
          };
        }
        if (this.selectedLocations) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              allowedLocations: this.selectedLocations.map((loc) => loc.id),
            },
          };
        }
        if (this.filterCategoryEquipment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              categoryEquipment: this.filterCategoryEquipment,
            },
          };
        }
        if (this.selectedCategoryEquipment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              categoryEquipment: this.selectedCategoryEquipment,
            },
          };
        }
        if (this.selectedIsInternal !== null) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              is_internal: this.selectedIsInternal,
            },
          };
        }
        if (this.selectedLoanStatus) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              loan_status: this.selectedLoanStatus,
            },
          };
        }

        await this.$store.dispatch("equipments/list", params);
        this.equipments = this.$store.getters["equipments/list"];
        this.total = this.$store.getters["equipments/listTotal"];
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async deleteEquipment(id) {
      const confirmation = await swal.fire({
        title: this.$t("EQUIPMENTS.DELETE_THIS_EQUIPMENT"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("equipments/destroy", id);
          await this.getList();
          this.$notify({
            type: "success",
            message: this.$t("EQUIPMENTS.EQUIPMENT_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    viewEquipment(equipment) {
      this.$router.push({
        name: "View Equipment",
        params: { id: equipment.id },
      });
    },

    async editEquipment(row) {
      this.$router.push({
        name: "Edit Equipment",
        params: { id: row.id },
      });
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
